import { Component, ElementRef, type OnInit, ViewChild } from '@angular/core'
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog'
import { TriggerApiCallsService } from '../../core/services/cancel-api-call'
import { ToastAlertComponent } from '../../03_shared-components/01_alerts/toast-alert/toast-alert.component'
import { GeneralAPIs } from '../../core/apis/general_api-calls'
import { SizeRangeAssignmentsAPIs } from '../../core/apis/size-range-assignment_api-calls'
import { type FormArray, FormBuilder, type FormGroup, Validators } from '@angular/forms'
import { COMMA, ENTER } from '@angular/cdk/keycodes'
import { type MatChipGrid } from '@angular/material/chips'
import { type MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'

@Component({
  selector: 'app-size-range-assignments-modal',
  templateUrl: './size-range-assignments-modal.component.html',
  styleUrl: './size-range-assignments-modal.component.scss'
})
export class SizeRangeAssignmentsModalComponent implements OnInit {
  loading: boolean = true
  loadingMessage: string = 'loading'
  sraForm: FormGroup
  filteredSizeRangeNames = this.config.data.sizeRangeNames
  filteredProducts = this.config.data.products
  filteredChannels = this.config.data.channels
  filteredHistoricalSizeRanges = this.config.data.sizeRangeNames

  @ViewChild('sizeRangeNameInput') sizeRangeNameInput: ElementRef<HTMLInputElement>
  @ViewChild('productInput') productInput: ElementRef<HTMLInputElement>
  @ViewChild('channelInput') channelInput: ElementRef<HTMLInputElement>
  @ViewChild('historicalSizeRangeInput') historicalSizeRangeInput: ElementRef<HTMLInputElement>

  readonly separatorKeyCodes: number[] = [ENTER, COMMA]

  constructor (
    private readonly dynamicDialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    public triggerApiCallsService: TriggerApiCallsService,
    public toastAlert: ToastAlertComponent,
    public generalAPIs: GeneralAPIs,
    private readonly sizeRangeAssignmentsAPIs: SizeRangeAssignmentsAPIs,
    private readonly formBuilder: FormBuilder
  ) {
    this.sraForm = this.formBuilder.group({
      size_range_name: ['', Validators.required],
      products: this.formBuilder.array([], Validators.required),
      channels: this.formBuilder.array([], Validators.required),
      is_future: [false],
      historical_size_range: [{ value: '', disabled: true }]
    })
  }

  ngOnInit (): void {
    this.sraForm.get('is_future').valueChanges.subscribe((isFuture: boolean) => {
      const historicalSizeRange = this.sraForm.get('historical_size_range')

      if (isFuture) {
        historicalSizeRange.addValidators(Validators.required)
        historicalSizeRange.enable()
      } else {
        historicalSizeRange.reset()
        historicalSizeRange.clearValidators()
        historicalSizeRange.disable()
      }
    })

    this.loading = false
  }

  get products (): FormArray {
    return this.sraForm.get('products') as FormArray
  }

  get channels (): FormArray {
    return this.sraForm.get('channels') as FormArray
  }

  removeProduct (product): void {
    this.products.removeAt(this.products.value.indexOf(product))
  }

  removeChannel (channel): void {
    this.channels.removeAt(this.channels.value.indexOf(channel))
  }

  productSelected (event: MatAutocompleteSelectedEvent): void {
    if (this.products.value.includes(event.option.viewValue)) {
      this.products.removeAt(this.products.value.indexOf(event.option.viewValue))
    } else {
      this.products.push(this.formBuilder.control(event.option.viewValue))
    }

    event.option.deselect()
  }

  channelSelected (event: MatAutocompleteSelectedEvent): void {
    if (this.channels.value.includes(event.option.viewValue)) {
      this.channels.removeAt(this.channels.value.indexOf(event.option.viewValue))
    } else {
      this.channels.push(this.formBuilder.control(event.option.viewValue))
    }

    event.option.deselect()
  }

  filterSizeRangeNames () {
    const value = this.sizeRangeNameInput.nativeElement.value.toLowerCase()
    this.filteredSizeRangeNames = this.config.data.sizeRangeNames.filter(option =>
      option.toLowerCase().includes(value)
    )
  }

  filterProducts () {
    const value = this.productInput.nativeElement.value.toLowerCase()
    this.filteredProducts = this.config.data.products.filter(option => option.toLowerCase().includes(value))
  }

  filterChannels () {
    const value = this.channelInput.nativeElement.value.toLowerCase()
    this.filteredChannels = this.config.data.channels.filter(option => option.toLowerCase().includes(value))
  }

  filterHistoricalSizeRanges () {
    const value = this.historicalSizeRangeInput.nativeElement.value.toLowerCase()
    this.filteredHistoricalSizeRanges = this.config.data.sizeRangeNames.filter(option =>
      option.toLowerCase().includes(value)
    )
  }

  close (data?: any) {
    if (this.dynamicDialogRef) {
      this.dynamicDialogRef.close(data || null)
    }
  }

  reset () {
    this.sraForm.reset()
    this.products.clear()
    this.channels.clear()
  }

  async onSubmit () {
    const assignments = this.sraForm.value.products.flatMap(product =>
      this.sraForm.value.channels.map(channel => ({
        size_range_name: this.sraForm.value.size_range_name,
        is_future: this.sraForm.value.is_future,
        historical_size_range: this.sraForm.value.historical_size_range || null,
        product_id: product,
        eligible_channel: channel,
        in_use: true
      }))
    )

    await this.sizeRangeAssignmentsAPIs.CreateSizeRangeAssignments(assignments)

    this.close()
  }
}
